import { RecipeStock } from "@/client/api";
import { toDateStr } from "@/shared/date";
import { APP_LOCALE } from "@/shared/locale";

export interface StockInfo {
  servings: number;
  oldestBatchId: string | null;
}
export type StockMap = Map<string, StockInfo>;

export function buildStockMap(stock: RecipeStock[]): StockMap {
  const map = new Map<string, StockInfo>();
  for (const r of stock) {
    const oldest = r.batches
      .filter((b) => b.servings_remaining > 0)
      .sort((a, b) => a.prep_date.localeCompare(b.prep_date))[0];
    map.set(r.recipe_id, {
      servings: r.total_servings_remaining,
      oldestBatchId: oldest?.batch_id ?? null,
    });
  }
  return map;
}

export interface MissingRecipe {
  recipe_id: string;
  recipe_name: string;
  recipe_type: string;
  deficit: number;
}

export interface UnplannedConsumptionRow {
  recipe_id: string;
  recipe_name: string;
  servings: number;
  /** Individual consumption IDs that make up this row */
  consumption_ids: string[];
}

export const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;
export const DAYS_ES: Record<string, string> = {
  Monday: "Lun",
  Tuesday: "Mar",
  Wednesday: "Mié",
  Thursday: "Jue",
  Friday: "Vie",
  Saturday: "Sáb",
  Sunday: "Dom",
};
export const DAYS_ES_FULL: Record<string, string> = {
  Monday: "Lunes",
  Tuesday: "Martes",
  Wednesday: "Miércoles",
  Thursday: "Jueves",
  Friday: "Viernes",
  Saturday: "Sábado",
  Sunday: "Domingo",
};

export function todayDayOfWeek(): string {
  return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][
    new Date().getDay()
  ]!;
}

export function currentMonday(): string {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);
  return toDateStr(monday);
}

export function addWeeks(dateStr: string, n: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + n * 7);
  return toDateStr(d);
}

export function getDayDate(startDate: string, dayIndex: number): number {
  const d = new Date(startDate + "T00:00:00");
  d.setDate(d.getDate() + dayIndex);
  return d.getDate();
}

export function formatWeekRange(startDate: string): string {
  const start = new Date(startDate + "T00:00:00");
  const end = new Date(startDate + "T00:00:00");
  end.setDate(start.getDate() + 6);
  const sameMonth = start.getMonth() === end.getMonth();
  if (sameMonth) {
    return `${start.getDate()}–${end.getDate()} de ${start.toLocaleDateString(APP_LOCALE, { month: "long" })}`;
  }
  return `${start.getDate()} ${start.toLocaleDateString(APP_LOCALE, { month: "short" })} – ${end.getDate()} ${end.toLocaleDateString(APP_LOCALE, { month: "short" })}`;
}

export function isNotPastWeek(startDate: string): boolean {
  return startDate >= currentMonday();
}

export function dateStrForDayIndex(startDate: string, dayIndex: number): string {
  const d = new Date(startDate + "T00:00:00");
  d.setDate(d.getDate() + dayIndex);
  return toDateStr(d);
}

/** Sum total consumption per recipe across all days in the consumptionMap */
export function totalConsumedPerRecipe(consumptionMap: Map<string, number>): Map<string, number> {
  const totals = new Map<string, number>();
  for (const [key, servings] of consumptionMap) {
    const recipeId = key.split(":")[0]!;
    totals.set(recipeId, (totals.get(recipeId) ?? 0) + servings);
  }
  return totals;
}
